/**
 * Gmail label color utilities
 * Provides consistent color handling across the app
 * Uses Gmail API accepted colors (different from Gmail UI colors)
 */

// Gmail API accepted colors - ONLY these work with the Gmail API
// Note: Gmail's UI shows different colors but API only accepts these
export const GMAIL_LABEL_COLORS: Array<{ bg: string; text: string }> = [
  // Light pastel backgrounds with black text
  { bg: '#efefef', text: '#000000' }, // Light Gray
  { bg: '#e7e7e7', text: '#000000' }, // Gray
  { bg: '#f6c5be', text: '#000000' }, // Light Red/Salmon
  { bg: '#ffc8af', text: '#000000' }, // Peach
  { bg: '#ffdeb5', text: '#000000' }, // Light Orange
  { bg: '#fef1d1', text: '#000000' }, // Cream
  { bg: '#fbe983', text: '#000000' }, // Yellow
  { bg: '#fdedc1', text: '#000000' }, // Pale Yellow
  { bg: '#b3efd3', text: '#000000' }, // Light Mint
  { bg: '#a2dcc1', text: '#000000' }, // Mint
  { bg: '#b9e4d0', text: '#000000' }, // Light Green
  { bg: '#c6f3de', text: '#000000' }, // Pale Green
  { bg: '#a0eac9', text: '#000000' }, // Seafoam
  { bg: '#98d7e4', text: '#000000' }, // Light Teal
  { bg: '#c9daf8', text: '#000000' }, // Light Blue
  { bg: '#b6cff5', text: '#000000' }, // Sky Blue
  { bg: '#a4c2f4', text: '#000000' }, // Pale Blue
  { bg: '#e3d7ff', text: '#000000' }, // Light Lavender
  { bg: '#e4d7f5', text: '#000000' }, // Lavender
  { bg: '#d0bcf1', text: '#000000' }, // Light Purple
  { bg: '#fbd3e0', text: '#000000' }, // Light Pink
  { bg: '#fcdee8', text: '#000000' }, // Pale Pink
  { bg: '#fbc8d9', text: '#000000' }, // Pink
  { bg: '#ebdbde', text: '#000000' }, // Dusty Pink
  { bg: '#cca6ac', text: '#000000' }, // Mauve
  { bg: '#efa093', text: '#000000' }, // Coral
  { bg: '#ffd6a2', text: '#000000' }, // Apricot
  { bg: '#fce8b3', text: '#000000' }, // Light Yellow (API version)
  { bg: '#fce8b2', text: '#000000' }, // Light Yellow (UI version - maps to #fce8b3)
  { bg: '#fcda83', text: '#000000' }, // Gold
  { bg: '#89d3b2', text: '#000000' }, // Teal
  { bg: '#ffbc6b', text: '#000000' }, // Orange
  { bg: '#ffad46', text: '#000000' }, // Tangerine

  // Medium/dark backgrounds with white text
  { bg: '#434343', text: '#ffffff' }, // Dark Gray
  { bg: '#666666', text: '#ffffff' }, // Medium Gray
  { bg: '#fb4c2f', text: '#ffffff' }, // Bright Red
  { bg: '#e66550', text: '#ffffff' }, // Red
  { bg: '#cc3a21', text: '#ffffff' }, // Dark Red
  { bg: '#ac2b16', text: '#ffffff' }, // Maroon
  { bg: '#822111', text: '#ffffff' }, // Burgundy
  { bg: '#ff7537', text: '#ffffff' }, // Bright Orange
  { bg: '#ffad47', text: '#ffffff' }, // Orange
  { bg: '#fad165', text: '#ffffff' }, // Yellow
  { bg: '#f2c960', text: '#ffffff' }, // Mustard
  { bg: '#d5ae49', text: '#ffffff' }, // Dark Yellow
  { bg: '#aa8831', text: '#ffffff' }, // Olive
  { bg: '#16a766', text: '#ffffff' }, // Green
  { bg: '#43d692', text: '#ffffff' }, // Bright Green
  { bg: '#44b984', text: '#ffffff' }, // Medium Green
  { bg: '#68dfa9', text: '#ffffff' }, // Light Green
  { bg: '#3dc789', text: '#ffffff' }, // Teal Green
  { bg: '#149e60', text: '#ffffff' }, // Forest Green
  { bg: '#2a9c68', text: '#ffffff' }, // Dark Teal
  { bg: '#0b804b', text: '#ffffff' }, // Deep Green
  { bg: '#1a764d', text: '#ffffff' }, // Dark Green
  { bg: '#076239', text: '#ffffff' }, // Very Dark Green
  { bg: '#2da2bb', text: '#ffffff' }, // Cyan
  { bg: '#4a86e8', text: '#ffffff' }, // Blue
  { bg: '#4986e7', text: '#ffffff' }, // Blue (alt)
  { bg: '#6d9eeb', text: '#ffffff' }, // Light Blue
  { bg: '#3c78d8', text: '#ffffff' }, // Medium Blue
  { bg: '#285bac', text: '#ffffff' }, // Dark Blue
  { bg: '#1c4587', text: '#ffffff' }, // Navy
  { bg: '#a479e2', text: '#ffffff' }, // Purple
  { bg: '#b694e8', text: '#ffffff' }, // Light Purple
  { bg: '#b99aff', text: '#ffffff' }, // Lavender
  { bg: '#8e63ce', text: '#ffffff' }, // Medium Purple
  { bg: '#653e9b', text: '#ffffff' }, // Dark Purple
  { bg: '#41236d', text: '#ffffff' }, // Deep Purple
  { bg: '#f691b2', text: '#ffffff' }, // Pink
  { bg: '#f7a7c0', text: '#ffffff' }, // Light Pink
  { bg: '#e07798', text: '#ffffff' }, // Medium Pink
  { bg: '#b65775', text: '#ffffff' }, // Dark Pink
  { bg: '#994a64', text: '#ffffff' }, // Rose
  { bg: '#83334c', text: '#ffffff' }, // Dark Rose
];

/**
 * Get the appropriate text color for a given background color
 * Returns the Gmail-standard text color for that background
 */
export function getTextColorForBackground(backgroundColor: string): string {
  const normalizedBg = backgroundColor.toLowerCase();
  const match = GMAIL_LABEL_COLORS.find(c => c.bg === normalizedBg);
  if (match) return match.text;

  // Fallback: calculate based on luminance
  const hex = normalizedBg.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#ffffff';
}
